import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { t as translate } from "../i18n/translations.js";
import ProblemReportLink from '../components/ProblemReportLink.jsx';

const API_BASE = "/api";

const DEFAULTS = {
  primaryColor: "#6366f1", secondaryColor: "#8b5cf6", accentColor: "#10b981",
  backgroundColor: "#f8f7ff", textColor: "#1a1a1a",
  fontFamily: "Inter, -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
  borderRadius: "20", buttonStyle: "gradient",
};

function getToken() { return localStorage.getItem("zyncfit_customer_token"); }

async function apiFetch(path) {
  const token = getToken();
  const res = await fetch(`${API_BASE}${path}`, {
    headers: { "Content-Type": "application/json", ...(token ? { Authorization: `Bearer ${token}` } : {}) },
  });
  const data = await res.json();
  if (!res.ok) throw new Error(data.error || "Er ging iets mis");
  return data;
}

function b(branding) { return { ...DEFAULTS, ...branding }; }

function btnStyle(br, override) {
  const r = parseInt(br.borderRadius || "20");
  const base = {
    display: "block", width: "100%", padding: "16px 24px", fontSize: 16,
    fontWeight: 700, borderRadius: r, cursor: "pointer", textAlign: "center",
    textDecoration: "none", boxSizing: "border-box", fontFamily: br.fontFamily,
    WebkitTapHighlightColor: "transparent", transition: "all 0.2s ease",
  };
  if (br.buttonStyle === "outline")
    return { ...base, background: "transparent", color: br.primaryColor, border: `2px solid ${br.primaryColor}`, ...override };
  if (br.buttonStyle === "solid")
    return { ...base, background: br.primaryColor, color: "#fff", border: "none", ...override };
  return { ...base, background: `linear-gradient(135deg, ${br.primaryColor}, ${br.secondaryColor})`, color: "#fff", border: "none", ...override };
}

export default function CustomerSessionDetailPage() {
  const { sessionId } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [branding, setBranding] = useState(null);
  const [imgLoaded, setImgLoaded] = useState(false);
  const [lang, setLang] = useState("nl");

  useEffect(() => {
    if (!getToken()) { navigate("/my-outfits", { replace: true }); return; }
    apiFetch(`/customer/sessions/${sessionId}`)
      .then((d) => {
        setData(d);
        // Fetch branding for this shop
        if (d.session?.shopDomain) {
          fetch(`${API_BASE}/branding/public/${encodeURIComponent(d.session.shopDomain)}`)
            .then((r) => r.json()).then(setBranding).catch(() => {});
          fetch(`${API_BASE}/settings/public/${encodeURIComponent(d.session.shopDomain)}`)
            .then((r) => r.json()).then((s) => { if (s.language) setLang(s.language); }).catch(() => {});
        }
      })
      .catch((err) => setError(err.message))
      .finally(() => setLoading(false));
  }, [sessionId, navigate]);

  const br = b(branding || {});
  const radius = parseInt(br.borderRadius);

  const card = { background: "#fff", borderRadius: radius, marginBottom: 20, overflow: "hidden", boxShadow: "0 1px 3px rgba(0,0,0,0.04), 0 4px 20px rgba(0,0,0,0.06)", animation: "fadeUp 0.5s ease both" };
  const cardInner = { padding: "20px 24px" };
  const label = { fontSize: 11, fontWeight: 700, textTransform: "uppercase", letterSpacing: 1.2, color: br.primaryColor, marginBottom: 14, display: "flex", alignItems: "center", gap: 6 };

  if (loading) {
    return (
      <div style={{ display: "flex", justifyContent: "center", alignItems: "center", minHeight: "100dvh", background: br.backgroundColor }}>
        <style>{CSS_ANIMATIONS}</style>
        <div style={{ textAlign: "center" }}>
          <div style={{ width: 48, height: 48, borderRadius: "50%", border: `3px solid ${br.primaryColor}`, borderTopColor: "transparent", animation: "spin 0.8s linear infinite", margin: "0 auto 16px" }} />
          <p style={{ color: "#666", fontSize: 14, fontFamily: br.fontFamily }}>{translate(lang, "common.loading")}</p>
        </div>
      </div>
    );
  }

  if (error || !data) {
    return (
      <div style={{ minHeight: "100dvh", background: br.backgroundColor, fontFamily: br.fontFamily, color: br.textColor }}>
        <style>{CSS_ANIMATIONS}</style>
        <div style={{ maxWidth: 640, margin: "0 auto", padding: "0 16px" }}>
          <button onClick={() => navigate("/my-outfits")} style={{ display: "inline-flex", alignItems: "center", gap: 6, fontSize: 14, color: br.primaryColor, background: "none", border: "none", cursor: "pointer", padding: "16px 0", fontWeight: 600 }}>
            {translate(lang, "sessionDetail.backToOverview")}
          </button>
          <ProblemReportLink style={{ color: br.primaryColor, marginLeft: 16 }} />
          <div style={{ textAlign: "center", padding: "40px 20px" }}>
            <h2 style={{ margin: "0 0 8px", fontWeight: 700 }}>{translate(lang, "sessionDetail.notFound")}</h2>
            <p style={{ color: "#666" }}>{error || translate(lang, "sessionDetail.notFoundDesc")}</p>
          </div>
        </div>
      </div>
    );
  }

  const { session, product, orderUrl: rawOrderUrl, orderText, productUrl } = data;
  const shopUrl = session.shopDomain ? `https://${session.shopDomain}` : null;
  const orderUrl = rawOrderUrl || productUrl || shopUrl;

  const formatDate = (d) => {
    try {
      return new Date(d).toLocaleDateString("nl-NL", { day: "numeric", month: "long", year: "numeric", hour: "2-digit", minute: "2-digit" });
    } catch { return ""; }
  };

  return (
    <div style={{ minHeight: "100dvh", background: br.backgroundColor, fontFamily: br.fontFamily, color: br.textColor }}>
      <style>{CSS_ANIMATIONS}</style>

      <div style={{ maxWidth: 640, margin: "0 auto", padding: "0 16px 100px" }}>
        {/* Back button */}
        <button onClick={() => navigate("/my-outfits")} style={{ display: "inline-flex", alignItems: "center", gap: 6, fontSize: 14, color: br.primaryColor, background: "none", border: "none", cursor: "pointer", padding: "16px 0", fontWeight: 600, fontFamily: br.fontFamily }}>
          {translate(lang, "sessionDetail.backToOverview")}
        </button>
        <ProblemReportLink style={{ color: br.primaryColor, marginLeft: 16 }} />

        {/* Header */}
        <div style={{ textAlign: "center", padding: "8px 0 24px", animation: "fadeUp 0.4s ease both" }}>
          <h1 style={{ fontSize: 24, fontWeight: 800, margin: 0, lineHeight: 1.25, letterSpacing: -0.3 }}>{product.title}</h1>
          <p style={{ fontSize: 14, color: `${br.textColor}80`, margin: "6px 0 0" }}>{formatDate(session.createdAt)}</p>
        </div>

        {/* AI Generated Image */}
        {session.resultImageB64 && (
          <div style={{ ...card, animationDelay: "0.1s" }}>
            <div style={cardInner}><div style={label}>{translate(lang, "sessionDetail.yourLook")}</div></div>
            <div style={{ background: `linear-gradient(135deg, ${br.primaryColor}08, ${br.secondaryColor}08)` }}>
              <img
                src={`data:${session.resultImageB64.startsWith("/9j/") ? "image/jpeg" : "image/png"};base64,${session.resultImageB64}`}
                alt="Virtueel passen resultaat"
                onLoad={() => setImgLoaded(true)}
                style={{ width: "100%", display: "block", maxHeight: 700, objectFit: "contain", opacity: imgLoaded ? 1 : 0, transition: "opacity 0.5s ease" }}
              />
            </div>
          </div>
        )}

        {/* Style advice */}
        {session.resultDescription && (
          <div style={{ ...card, animationDelay: "0.2s" }}>
            <div style={cardInner}>
              <div style={label}>{translate(lang, "sessionDetail.styleAdvice")}</div>
              <p style={{ fontSize: 15, lineHeight: 1.8, color: `${br.textColor}cc`, whiteSpace: "pre-line", margin: 0 }}>{session.resultDescription}</p>
            </div>
          </div>
        )}

        {/* Original photos */}
        <div style={{ ...card, animationDelay: "0.3s" }}>
          <div style={cardInner}>
            <div style={label}>{session.resultImageB64 ? translate(lang, "sessionDetail.originalPhotos") : translate(lang, "sessionDetail.yourPhotoAndArticle")}</div>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
              {session.customerPhoto && <img src={`data:image/jpeg;base64,${session.customerPhoto}`} alt={translate(lang, "landing.yourPhoto")} style={{ width: "100%", borderRadius: radius * 0.6, display: "block" }} />}
              {(product.garmentImageUrl || product.imageUrl) && <img src={product.garmentImageUrl || product.imageUrl} alt={product.title} style={{ width: "100%", borderRadius: radius * 0.6, display: "block" }} />}
            </div>
          </div>
        </div>

        {/* Inline order CTA */}
        {orderUrl && (
          <a href={orderUrl} target="_blank" rel="noopener noreferrer" style={{ ...btnStyle(br), marginBottom: 20, animation: "fadeUp 0.5s ease both", animationDelay: "0.35s" }}>
            {orderText || translate(lang, "sessionDetail.orderOutfit")}
          </a>
        )}

        <div style={{ textAlign: "center", padding: "28px 0 16px", color: `${br.textColor}40`, fontSize: 12 }}>{translate(lang, "common.poweredBy")}</div>
      </div>

      {/* Floating order button */}
      {orderUrl && (
        <a href={orderUrl} target="_blank" rel="noopener noreferrer"
          style={{ position: "fixed", bottom: 20, left: "50%", transform: "translateX(-50%)", width: "calc(100% - 32px)", maxWidth: 608, ...btnStyle(br, { padding: "18px 24px", fontSize: 17 }), boxShadow: `0 4px 24px ${br.primaryColor}40, 0 2px 8px rgba(0,0,0,0.12)`, zIndex: 1000 }}>
          {translate(lang, "sessionDetail.orderOutfit")}
        </a>
      )}
    </div>
  );
}

const CSS_ANIMATIONS = `
@keyframes spin{to{transform:rotate(360deg)}}
@keyframes fadeUp{from{opacity:0;transform:translateY(20px)}to{opacity:1;transform:translateY(0)}}
@import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700;800&family=Playfair+Display:wght@700&family=Poppins:wght@400;600;700&family=Montserrat:wght@400;600;700&display=swap');
`;
